// src/components/calendar/CalendarViews/DayViewShadcn.jsx - FIXED: Proper positioning without double absolute
import React from 'react';
import { Spinner } from '../../ui/spinner';

// ✅ KEEP: Import our shadcn day components
import {
  DayViewHeaderShadcn,
  DayViewTimeRowShadcn,
  DayViewEventCardShadcn
} from '../../ui/calendar/day';

const DayViewShadcn = ({
  currentDate,
  previewEvent,
  onTimeSlotClick,
  onEventClick,
  isAdmin,
  showEventPanel,

  // ✅ NEW: Accept events data as props instead of useEvents hook
  events = [],
  loading = false,
  error = null,

  // creation mode props
  isInCreationMode = false,

  availabilityDates,
  viewingPartnerAvailability,

  // Time range configuration
  timeRange = { start: 8, end: 18 }  // Default: 8 AM - 6 PM
}) => {

  // ✅ KEEP: Business hours configuration - now from props
  const BUSINESS_HOURS = {
    start: timeRange.start,
    end: timeRange.end,
  };

  // Fix getEventsForDate
  const getEventsForDate = (dateStr) => {
    return events.filter(event => {
      if (!event.start) return false;
      // FIXED: Use timezone-safe conversion for events
      const eventYear = event.start.getFullYear();
      const eventMonth = String(event.start.getMonth() + 1).padStart(2, '0');
      const eventDay = String(event.start.getDate()).padStart(2, '0');
      const eventDateStr = `${eventYear}-${eventMonth}-${eventDay}`;
      return eventDateStr === dateStr;
    });
  };

  // Fix getAllEventsForDate
  const getAllEventsForDate = (date) => {
    // FIXED: Use timezone-safe conversion for date parameter
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    const eventsForDate = getEventsForDate(dateStr);
    const allEvents = eventsForDate;

    return allEvents.filter(event => {
      const eventDate = event.start.toDateString();
      const targetDate = date.toDateString();
      return eventDate === targetDate;
    });
  };

  // ✅ KEEP: Get events for specific time slot (IMPORTANT FUNCTION)
  const getEventsForTimeSlot = (date, hour) => {
    const allEvents = getAllEventsForDate(date);

    return allEvents.filter(event => {
      const eventStartHour = event.start.getHours();
      return eventStartHour === hour;
    });
  };

  // ✅ KEEP: Side-by-side layout calculation (CRITICAL COMPLEX ALGORITHM)
  const calculateSideBySideLayout = (events) => {
    if (events.length <= 1) {
      return events.map(event => ({ ...event, columnIndex: 0, totalColumns: 1 }));
    }

    // Sort events by start time, then by duration (longer events first)
    const sortedEvents = [...events].sort((a, b) => {
      const aStart = a.start.getHours() * 60 + a.start.getMinutes();
      const bStart = b.start.getHours() * 60 + b.start.getMinutes();
      if (aStart !== bStart) return aStart - bStart;

      const aDuration = (a.end.getTime() - a.start.getTime()) / (1000 * 60);
      const bDuration = (b.end.getTime() - b.start.getTime()) / (1000 * 60);
      return bDuration - aDuration;
    });

    // Check if two events overlap in time
    const eventsOverlap = (event1, event2) => {
      return event1.start < event2.end && event2.start < event1.end;
    };

    // Assign column positions
    const layoutEvents = [];
    const columns = [];

    sortedEvents.forEach(event => {
      // Find the first available column (leftmost)
      let columnIndex = 0;
      while (columnIndex < columns.length) {
        const columnEvents = columns[columnIndex];
        const hasOverlap = columnEvents.some(existingEvent => eventsOverlap(event, existingEvent));
        if (!hasOverlap) break;
        columnIndex++;
      }

      // Create new column if needed
      if (columnIndex === columns.length) {
        columns.push([]);
      }

      // Add event to column
      columns[columnIndex].push(event);
      layoutEvents.push({
        ...event,
        columnIndex,
        totalColumns: Math.max(columns.length, layoutEvents[0]?.totalColumns || 1)
      });
    });

    // Update all events with final column count
    return layoutEvents.map(event => ({
      ...event,
      totalColumns: columns.length
    }));
  };

  // ✅ FIXED: Event positioning calculation - allows spanning multiple hours
  const calculateEventStyle = (event) => {
    const startMinutes = event.start.getMinutes();
    const durationMinutes = (event.end.getTime() - event.start.getTime()) / (1000 * 60);

    // Calculate position within the starting hour slot
    const topOffset = (startMinutes / 60) * 100;

    // ✅ FIXED: Remove Math.min constraint - let events span multiple hours
    // For 2-hour event: (120 minutes / 60) * 100 = 200%
    const height = (durationMinutes / 60) * 100;

    return {
      top: `${topOffset}%`,
      height: `${height}%`, // ← Now allows 200%, 300%, etc. for multi-hour events
      zIndex: event.isPreview ? 999 : 10 + (event.columnIndex || 0)
    };
  };

  const hours = Array.from({ length: BUSINESS_HOURS.end - BUSINESS_HOURS.start }, (_, i) => BUSINESS_HOURS.start + i);

  // ✅ NEW: Calculate layout for ALL DAY's events (not per-hour)
  const allDayEvents = getAllEventsForDate(currentDate);
  const allEventsWithLayout = React.useMemo(() => {
    return calculateSideBySideLayout(allDayEvents);
  }, [allDayEvents]);

  // ✅ UPDATED: Use passed loading state instead of hook
  if (loading && !events.length) {
    return (
      <div className="day-view flex flex-col h-full">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Spinner className="size-8 text-blue-500 mx-auto mb-2" />
            <p className="text-slate-400 text-sm">Loading calendar...</p>
          </div>
        </div>
      </div>
    );
  }

  // ✅ UPDATED: Use passed error state instead of hook
  if (error) {
    return (
      <div className="day-view flex flex-col h-full">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-400 text-sm">Error loading calendar: {error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-2 px-3 py-1 bg-slate-700 text-slate-300 rounded text-sm hover:bg-slate-600"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ✅ FIXED: Complete JSX with proper positioning using shadcn components
  return (
    <div className="day-view flex flex-col h-full">
      {/* ✅ KEEP: Day header using shadcn component */}
      <DayViewHeaderShadcn currentDate={currentDate} />

      {/* ✅ KEEP: Day grid using shadcn time row components with full functionality */}
      <div className="flex-1 overflow-auto">
        {hours.map(hour => {
          const timeSlotEvents = getEventsForTimeSlot(currentDate, hour);

          // FIXED: Calculate hasAvailability (same as MonthView/WeekView)
          const dateStr = currentDate.toISOString().split('T')[0];
          const hasAvailability = viewingPartnerAvailability && availabilityDates && availabilityDates.has(dateStr);

          return (
            <DayViewTimeRowShadcn
              key={hour}
              hour={hour}
              currentDate={currentDate}
              events={timeSlotEvents}
              onTimeSlotClick={onTimeSlotClick}
              isAdmin={isAdmin}
              hasAvailability={hasAvailability}  // ← Now properly defined!
              viewingPartnerAvailability={viewingPartnerAvailability}
            >
              {/* ✅ FIXED: Render events that START in this hour, using day-level column assignments */}
              {timeSlotEvents.length > 0 && (() => {
                // Get layout info from day-level calculation
                const eventsToRender = timeSlotEvents.map(timeSlotEvent => {
                  return allEventsWithLayout.find(e => e.id === timeSlotEvent.id) || timeSlotEvent;
                });

                return eventsToRender.map((event, eventIndex) => {
                  const eventStyle = calculateEventStyle(event);

                  return (
                    <div
                      key={eventIndex}
                      className="absolute"
                      style={{
                        // ✅ FIXED: Use columnIndex from day-level calculation
                        left: `${(event.columnIndex || 0) * 210}px`,  // 200px card + 10px gap
                        width: '200px',  // Fixed card width - doesn't shrink
                        ...eventStyle // top, height, zIndex from calculateEventStyle
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (showEventPanel) {
                          onTimeSlotClick(currentDate, hour);
                        } else if (!event.isPreview) {
                          onEventClick(event);
                        }
                      }}
                    >
                      <DayViewEventCardShadcn
                        event={event}
                        onClick={onEventClick}
                        showEventPanel={showEventPanel}
                      />
                    </div>
                  );
                });
              })()}
            </DayViewTimeRowShadcn>
          );
        })}
      </div>
    </div>
  );
};

export default DayViewShadcn;